import { useEffect, useState } from 'react';

/**
 * Converts width value to pixels
 * @param {string|number} width - Width value (can be "100px", "50%", or 100)
 * @param {number} measuredWidth - Measured width in pixels (used for percentage conversion)
 * @returns {number|null} - Numeric width in pixels, or null if invalid
 */
const convertWidthToPixels = (width, measuredWidth) => {
  if (!width) return null;

  // If it's already a number, return it
  if (typeof width === 'number') {
    return width;
  }

  // If it's a string, parse it
  if (typeof width === 'string') {
    const widthString = width.trim();

    // Handle px suffix (e.g., "100px" -> 100)
    if (widthString.endsWith('px')) {
      return parseFloat(widthString);
    }

    // Handle % suffix (e.g., "50%" -> measuredWidth * 0.5)
    if (widthString.endsWith('%')) {
      const percentageValue = parseFloat(widthString);
      return isNaN(percentageValue) ? null : (percentageValue / 100) * measuredWidth;
    }

    return parseFloat(widthString);
  }

  return null;
};

export const useVwToPx = (props) => {
  const [fontSizeInPx, setFontSizeInPx] = useState(0);
  const { vwValue, parentId, maxWidth = 600, ref, width } = props;
  useEffect(() => {
    if (typeof window === 'undefined' || !('ResizeObserver' in window)) return;

    const board = (parentId ? document.getElementById(parentId) : ref.current) || window;

    const computeFontSize = () => {
      const measuredWidth = board?.getBoundingClientRect().width || 0;

      // Convert width to pixels using the helper function
      const numericWidth = convertWidthToPixels(width, measuredWidth);
      const boardWidth = numericWidth ? Math.max(measuredWidth, numericWidth) : measuredWidth;
      const windowWidth = Math.min(boardWidth, maxWidth);

      // Validate vwValue is a number
      const numericVwValue = typeof vwValue === 'number' ? vwValue : parseFloat(vwValue);
      if (isNaN(numericVwValue) || isNaN(windowWidth)) {
        return; // Don't update state if calculation would be NaN
      }

      const pixelValue = (numericVwValue / 100) * windowWidth;
      setFontSizeInPx(Math.round(pixelValue));
    };

    const resizeObserver = new ResizeObserver(computeFontSize);
    if (board) {
      resizeObserver.observe(board);
    }

    // Initial computation
    computeFontSize();

    window.addEventListener('resize', computeFontSize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', computeFontSize);
      if (board) {
        resizeObserver.unobserve(board);
      }
    };
  }, [maxWidth, parentId, ref, vwValue, width]);

  return fontSizeInPx;
};
